/**
 * SRV-F2-4: Cost Allocation Section
 * Customer-facing vs internal cost breakdown a worksheet detail oldalon.
 * V.9.3: Per-equipment költség allokáció.
 */

import {
  getWorksheetItems,
  getWorksheetSummary,
  type WorksheetItem,
  type WorksheetSummary,
} from '@/api/worksheets';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

interface CostAllocationSectionProps {
  worksheetId: string;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);

export function CostAllocationSection({ worksheetId }: CostAllocationSectionProps) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<WorksheetSummary | null>(null);
  const [items, setItems] = useState<WorksheetItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [summaryRes, itemsRes] = await Promise.all([
        getWorksheetSummary(worksheetId),
        getWorksheetItems(worksheetId),
      ]);
      setSummary(summaryRes.data);
      setItems(itemsRes.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  if (loading || !summary) return null;

  // Separate customer-facing vs internal items
  const customerItems = items.filter(i => i.itemType !== 'EXTERNAL_COST');
  const internalItems = items.filter(i => i.itemType === 'EXTERNAL_COST');

  const customerTotal = customerItems.reduce((s, i) => s + i.totalPrice, 0);
  const internalTotal = internalItems.reduce((s, i) => s + i.totalPrice, 0);

  const hasCredit = summary.inspectionFeeCredit != null;
  const creditAmount = summary.inspectionFeeCredit?.amount ?? 0;
  const finalCustomerTotal = customerTotal + creditAmount;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('worksheet.costAllocation.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Customer-facing breakdown */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            {t('worksheet.costAllocation.customerFacing')}
          </h4>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>{t('service.parts')}</span>
              <span>{formatCurrency(summary.partsNetAmount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>{t('service.laborCost')}</span>
              <span>{formatCurrency(summary.laborNetAmount)}</span>
            </div>
            {summary.otherNetAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span>{t('service.itemTypeOther')}</span>
                <span>{formatCurrency(summary.otherNetAmount)}</span>
              </div>
            )}
            {hasCredit && (
              <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                <span>{t('worksheet.inspectionFeeCredit.label')}</span>
                <span>{formatCurrency(creditAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold border-t border-gray-200 dark:border-gray-700 pt-1">
              <span>
                {hasCredit
                  ? t('worksheet.inspectionFeeCredit.netAfterCredit')
                  : t('worksheet.costAllocation.customerTotal')}
              </span>
              <span>{formatCurrency(hasCredit ? finalCustomerTotal : customerTotal)}</span>
            </div>
          </div>
        </div>

        {/* Internal costs (only if any) */}
        {internalItems.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              {t('worksheet.costAllocation.internal')}
            </h4>
            <div className="space-y-1">
              {internalItems.map(item => (
                <div
                  key={item.id}
                  className="flex justify-between text-sm text-orange-600 dark:text-orange-400"
                >
                  <span className="truncate mr-2">{item.description}</span>
                  <span className="flex-shrink-0">{formatCurrency(item.totalPrice)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-semibold border-t border-gray-200 dark:border-gray-700 pt-1">
                <span>{t('worksheet.costAllocation.internalTotal')}</span>
                <span className="text-orange-600 dark:text-orange-400">
                  {formatCurrency(internalTotal)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Grand total */}
        <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-2">
          <div className="flex justify-between font-bold">
            <span>{t('worksheet.costAllocation.grandTotal')}</span>
            <span>{formatCurrency(summary.totalGrossAmount)}</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('worksheet.costAllocation.vatIncluded')}</span>
            <span>{formatCurrency(summary.vatAmount)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
